import { MdOutlineReceiptLong } from 'react-icons/md';
import { inputStyles } from '~/components/Vendors/styles';
import { FundRequestExpenseTypes } from '~/enums/fundRequest';
import { formatDateIfValid, toUSD } from '~/utils';
import { UseFormReturn } from 'react-hook-form';
import { FaFlag, FaCheck } from 'react-icons/fa6';
import { RxCrossCircled } from 'react-icons/rx';
import { FiEdit2 } from 'react-icons/fi';
import { SelectOption } from '~/types/globals';

export const ExpenseItemRow = ({
    methods,
    formKey,
    description,
    date,
    amount,
    expenseCategory,
    glCodes,
    handleExpenseItemClick,
    handleDeleteItem,
    isEditable = true,
}: {
    methods: UseFormReturn<any>; // TODO: Replace any with proper type when available
    formKey: string;
    description: string;
    date: string;
    amount: number;
    glCode: string;
    glCodes: SelectOption[];
    expenseCategory: FundRequestExpenseTypes;
    handleExpenseItemClick: (
        expenseCategory: FundRequestExpenseTypes,
        formKey: string,
        shouldPreviewFirstDocument?: boolean
    ) => void;
    handleDeleteItem: (
        formKey: string,
        expenseCategory: FundRequestExpenseTypes
    ) => void;
    isEditable?: boolean;
}) => {
    const { register } = methods;
    const expenseItem = methods.getValues(formKey);
    
    let hasFlag = false;
    let hasResolvedFlag = false;
    
    if (expenseItem?.id) {
        hasFlag =
            !!expenseItem.flagged_comment && !expenseItem.flagged_resolved_date;
        hasResolvedFlag =
            !!expenseItem.flagged_comment && !!expenseItem.flagged_resolved_date;
    }

    const uploads = expenseItem?.uploads || [];

    const handleReceiptClick = () => {
        if (uploads.length > 0 && isEditable) {
            handleExpenseItemClick(expenseCategory, formKey, true);
        }
    };

    return (
        <div className='space-y-2'>
            <div className={'flex items-center justify-between gap-4'}>
                <div
                    className={`w-1/2 font-bold ${
                        hasFlag ? 'text-[#F02D3A]' : 'text-[#00AEB3]'
                    } ${
                        isEditable
                            ? 'cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                    }`}
                >
                    <RxCrossCircled
                        color='#F02D3A'
                        size={20}
                        className={`mb-1 mr-2 ${
                            isEditable ? 'cursor-pointer' : 'cursor-not-allowed'
                        }`}
                        onClick={() =>
                            isEditable && handleDeleteItem(formKey, expenseCategory)
                        }
                    />
                    {hasFlag && (
                        <FaFlag
                            className={`mb-1 mr-2 ${
                                isEditable ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                            onClick={() =>
                                isEditable &&
                                handleExpenseItemClick(expenseCategory, formKey)
                            }
                        />
                    )}
                    <p
                        className={`inline ${
                            isEditable ? 'cursor-pointer' : 'cursor-not-allowed'
                        }`}
                        onClick={() =>
                            isEditable &&
                            handleExpenseItemClick(expenseCategory, formKey)
                        }
                    >
                        {description}
                    </p>
                    <FiEdit2
                        size={16}
                        className={`mb-1 ml-2 ${
                            isEditable
                                ? 'cursor-pointer text-[#00AEB3] hover:text-[#007a7d]'
                                : 'cursor-not-allowed'
                        }`}
                        onClick={() =>
                            isEditable &&
                            handleExpenseItemClick(expenseCategory, formKey)
                        }
                        title='Edit expense'
                    />
                </div>

                <div
                    className={`w-22 text-right ${!isEditable ? 'opacity-50' : ''}`}
                >
                    {formatDateIfValid(date, 'MM/dd/yy')}
                </div>

                <div className='w-20 2xl:w-12'>
                    {uploads.length > 0 && (
                        <span>
                            <MdOutlineReceiptLong
                                className={`cursor-pointer ${
                                    !isEditable ? 'opacity-50' : ''
                                }`}
                                onClick={handleReceiptClick}
                            />
                        </span>
                    )}
                </div>

                <div
                    className={`flex w-72 items-center justify-end gap-4 ${
                        !isEditable ? 'opacity-50' : ''
                    }`}
                >
                    <span>{toUSD(amount)}</span>
                    <span>
                        <select
                            defaultValue={`${formKey}.gl_code`}
                            className={`${inputStyles} w-56 !p-[5px] ${
                                !isEditable ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            {...register(`${formKey}.gl_code`)}
                            disabled={!isEditable}
                        >
                            <option value='' disabled>
                                Select
                            </option>
                            {glCodes.map((c, i) => {
                                return (
                                    <option value={c.value} key={i}>
                                        {c.value} - {c.label}
                                    </option>
                                );
                            })}
                        </select>
                    </span>
                </div>
            </div>

            {/* Resolved Flag Display */}
            {hasResolvedFlag && (
                <div className='flex items-center space-x-2 px-3 py-2 rounded' style={{backgroundColor: '#f0f9f0'}}>
                    <div className='flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0' style={{backgroundColor: '#9CAF88'}}>
                        <span className='text-white text-xs font-bold'>✓</span>
                    </div>
                    <span className='text-sm text-gray-700 line-through'>
                        {expenseItem.flagged_comment}
                    </span>
                </div>
            )}
        </div>
    );
};
